// crewbit-admin/src/components/money/DeleteDraftInvoiceModal.tsx
//
// SPEC §Money.DraftDiscardActuallyDeletes (2026-05-06) D5 — confirmation modal
// for the detail-panel "Delete draft" action. No reason input (drafts aren't
// accounting documents — no audit trail to populate). Two buttons: Keep / Delete.
// Mirrors CancelInvoiceModal shape; admin parallel of mobile DeleteDraftInvoiceModal.

import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { palette, spacing, transition } from '../../styles/tokens';
import { deleteDraftInvoice } from '../../lib/invoiceMutations';

interface Props {
  isOpen: boolean;
  invoiceId: string;
  invoiceNumber: string | null;
  onClose: () => void;
  onResult: (ok: boolean, message: string) => void;
}

export function DeleteDraftInvoiceModal({ isOpen, invoiceId, invoiceNumber, onClose, onResult }: Props) {
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const onConfirm = async () => {
    setLoading(true);
    const result = await deleteDraftInvoice(invoiceId);
    setLoading(false);
    if (result.success) {
      onResult(true, 'Draft deleted.');
      onClose();
    } else {
      onResult(false, result.error.message ?? 'Failed to delete draft.');
    }
  };

  const numberLabel = invoiceNumber ?? 'this draft';

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, zIndex: 70,
        background: 'rgba(0,0,0,0.3)',
        backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%', maxWidth: 420, zIndex: 71,
        background: palette.white, borderRadius: 16,
        boxShadow: '0 16px 64px rgba(0,0,0,0.14), 0 4px 16px rgba(0,0,0,0.08)',
        padding: 28,
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 14,
          background: palette.redWash,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: spacing.md,
        }}>
          <Trash2 size={22} color={palette.red} strokeWidth={1.8} />
        </div>

        <div style={{ fontSize: 16, fontWeight: 700, color: palette.g800, marginBottom: 6 }}>
          Delete draft?
        </div>
        <div style={{ fontSize: 14, color: palette.g500, lineHeight: 1.5, marginBottom: spacing.md }}>
          Draft {numberLabel} will be permanently deleted. This can&apos;t be undone.
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: spacing.md }}>
          <button onClick={onClose} disabled={loading} style={{
            padding: '9px 22px', borderRadius: 20,
            border: `1px solid ${palette.g200}`, background: palette.white,
            fontSize: 13.5, fontWeight: 500, color: palette.g600,
            cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            transition: `background ${transition.fast}`,
          }}>Keep draft</button>
          <button onClick={onConfirm} disabled={loading} style={{
            padding: '9px 22px', borderRadius: 20, border: 'none',
            background: loading ? palette.g300 : palette.red, color: palette.white,
            fontSize: 13.5, fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
          }}>{loading ? 'Deleting…' : 'Delete'}</button>
        </div>
      </div>
    </>
  );
}
